import { useEffect, useState, useCallback } from 'react';
import { getCustomers, searchCustomerTypes } from 'api/customer';
import { createCustomer, updateCustomer } from 'api/customer';
import { makeCustomersFormat, filterCustomers } from 'containers/pages/customers/libs';
import { useNotificationProvider } from 'hooks';
import { Customer, CustomerType } from 'containers/pages/customers/types';

function useCustomers() {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchedCustomers, setSearchedCustomers] = useState<Customer[]>([]);
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [searchInput, setSearchInput] = useState('');

  // Toggle add form open/close
  const onToggleForm = () => {
    setIsOpenForm((prev) => !prev);
  };

  // Search handlers
  const onSearchInput = (input: string) => {
    setSearchInput(input);
    if (!input) {
      setSearchedCustomers(customers);
    } else {
      const filtered = filterCustomers(input, customers);
      setSearchedCustomers(filtered);
    }
  };

  const onResetSearch = () => {
    setSearchInput('');
    setSearchedCustomers(customers);
  };

  // Save handler (create or update)
  const onSaveCustomer = async (formData: Record<string, unknown>, isEdit: boolean) => {
    try {
      if (isEdit) {
        await updateCustomer(formData);
        notifySuccess('Customer updated successfully');
        // No list state update — avoids full table re-render flash.
        // The edit form already holds correct values via its own useReducer.
      } else {
        await createCustomer(formData);
        notifySuccess('Customer created successfully');
        await onRefreshCustomers();
        setIsOpenForm(false);
      }
    } catch (error) {
      console.error('[useCustomers] Save failed:', error);
      notifyError('Failed to save customer. Please try again.');
    }
  };

  // Refresh the customer list
  const onRefreshCustomers = useCallback(async () => {
    try {
      const result = await getCustomers({ SearchKeyword: '', Page: 1, Take: 1000 });
      const formatted = makeCustomersFormat(result);
      setCustomers(formatted);
      setSearchedCustomers(formatted);
      setSearchInput('');
    } catch (error) {
      console.error('[useCustomers] Refresh failed:', error);
      notifyError('Failed to refresh customers');
    }
  }, [notifyError]);

  // Initial data load
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [customerSettled, typesSettled] = await Promise.allSettled([
          getCustomers({ SearchKeyword: '', Page: 1, Take: 1000 }),
          searchCustomerTypes({ SearchKeyword: '', Take: 100 }),
        ]);

        if (customerSettled.status === 'fulfilled') {
          const formatted = makeCustomersFormat(customerSettled.value);
          setCustomers(formatted);
          setSearchedCustomers(formatted);
        } else {
          console.error('[useCustomers] Customers load failed:', customerSettled.reason);
          notifyError('Failed to load customers');
        }

        if (typesSettled.status === 'fulfilled') {
          const typesResult = typesSettled.value;
          const types = Array.isArray(typesResult) ? typesResult : typesResult?.Data || [];
          setCustomerTypes(types);
        } else {
          console.error('[useCustomers] Customer types load failed:', typesSettled.reason);
        }
      } catch (error) {
        console.error('[useCustomers] Initial load failed:', error);
        notifyError('Failed to load customers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [notifyError]);

  const state = {
    isLoading,
    isOpenForm,
    customers,
    searchedCustomers,
    customerTypes,
    searchInput,
  };

  const handler = {
    onToggleForm,
    onSearchInput,
    onResetSearch,
    onSaveCustomer,
    onRefreshCustomers,
  };

  return { state, handler };
}

export default useCustomers;
